import { useMemo, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "@/components/ui/sonner";
import {
  ArrowLeft,
  MapPin,
  Building2,
  CircleDot,
  Calendar,
  BookmarkPlus,
  BookmarkCheck,
  Share2,
  MessageCircle,
  CheckCircle2,
  FileText,
  Radar,
  Link2,
  TrendingUp,
  Clock,
  Coins,
  ShieldCheck,
  AlertTriangle,
  Target,
  Scale,
} from "lucide-react";
import { Header } from "@/components/homepage/Header";
import { Footer } from "@/components/homepage/Footer";
import { OpportunityCard } from "@/components/homepage/OpportunityCard";
import { ScoreBadge } from "@/components/homepage/ScoreBadge";
import { StatusBadge } from "@/components/StatusBadge";
import { IntelligenceMetric } from "@/components/IntelligenceMetric";
import { BackButton } from "@/components/BackButton";
import {
  EVIDENCE_CLASSES,
} from "@/components/EvidenceClass";
import { useOpportunity, resolveRelated } from "@/hooks/useOpportunity";
import { useOpportunities } from "@/hooks/useOpportunities";
import { isSaved, toggleSaved } from "@/utils/savedOpportunities";
import { deriveSubscores } from "@/utils/scoreBreakdown";
import { deriveEvidenceComposition } from "@/utils/evidence";
import {
  MARKET_IMPACT_MAP,
  TIME_SENSITIVITY_MAP,
} from "@/utils/intelligence";
import { DETAIL_PAGE } from "@/constants/testIds";

const RISK_TONE = {
  high: { icon: "bg-risk-500", text: "text-risk-700", bg: "bg-risk-50", border: "border-risk-100", label: "مرتفع" },
  medium: { icon: "bg-warning-500", text: "text-warning-700", bg: "bg-warning-50", border: "border-warning-100", label: "متوسط" },
  low: { icon: "bg-fursa-500", text: "text-fursa-700", bg: "bg-fursa-50", border: "border-fursa-100", label: "منخفض" },
};

/**
 * Table of Contents — sticky rail on the left of the dossier body.
 * Anchors sync with the section IDs rendered further down; the active
 * section is highlighted via IntersectionObserver.
 */
const TableOfContents = ({ items }) => {
  const [active, setActive] = useState(items[0]?.id);
  useEffect(() => {
    if (typeof window === "undefined" || !items.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the topmost visible section as active.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-96px 0px -70% 0px", threshold: [0, 0.25, 0.5] },
    );
    for (const it of items) {
      const el = document.getElementById(it.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [items]);

  return (
    <nav
      aria-label="محتويات الملف"
      data-testid="detail-toc"
      className="sticky top-24 hidden max-h-[calc(100vh-8rem)] overflow-auto lg:block"
    >
      <p className="mb-3 text-[10px] font-semibold uppercase tracking-widest text-fursa-700">
        محتويات الملف
      </p>
      <ol className="space-y-1 border-s border-surface-200 ps-4">
        {items.map((item) => {
          const isActive = item.id === active;
          return (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                data-testid={`detail-toc-${item.id}`}
                className={`group relative -ms-[17px] flex items-center gap-2 rounded ps-4 pe-2 py-1.5 text-sm transition-colors ${
                  isActive
                    ? "font-semibold text-fursa-700"
                    : "text-ink-500 hover:text-ink-900"
                }`}
              >
                <span
                  aria-hidden
                  className={`absolute end-full top-1/2 h-1.5 w-1.5 -translate-y-1/2 translate-x-[1px] rounded-full transition-all ${
                    isActive ? "bg-fursa-600" : "bg-transparent group-hover:bg-ink-300"
                  }`}
                />
                {item.label}
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

const SectionHeading = ({ eyebrow, title, icon: Icon, id }) => (
  <div id={id} className="mb-6 flex items-center gap-3 scroll-mt-24">
    {Icon && (
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-fursa-50 text-fursa-700">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </span>
    )}
    <div>
      {eyebrow && (
        <p className="text-[10px] font-semibold uppercase tracking-widest text-fursa-700">
          {eyebrow}
        </p>
      )}
      <h2 className="text-xl font-semibold tracking-tight text-ink-900 sm:text-2xl">
        {title}
      </h2>
    </div>
  </div>
);

/* ─── Loading skeleton — mirrors the hero + body layout so the
   perceived-load feels intentional, not empty. ───────────────── */
const DetailSkeleton = () => (
  <div className="min-h-screen bg-surface-50">
    <Header />
    <main data-testid={DETAIL_PAGE.loading} aria-busy="true" aria-live="polite">
      <section className="fursa-briefing-bg text-white">
        <div className="mx-auto max-w-7xl px-6 py-14 lg:px-8 lg:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 lg:gap-12">
            <div className="lg:col-span-2 space-y-5">
              <div className="skeleton h-6 w-40 rounded-full !bg-intel-800/40" />
              <div className="flex gap-2">
                <div className="skeleton h-6 w-24 rounded-full !bg-intel-800/40" />
                <div className="skeleton h-6 w-28 rounded-full !bg-intel-800/40" />
                <div className="skeleton h-6 w-32 rounded-full !bg-intel-800/40" />
              </div>
              <div className="skeleton h-12 w-4/5 rounded !bg-intel-800/40" />
              <div className="skeleton h-4 w-full rounded !bg-intel-800/40" />
              <div className="skeleton h-4 w-3/4 rounded !bg-intel-800/40" />
            </div>
            <div className="lg:col-span-1">
              <div className="rounded-2xl bg-white p-6 space-y-4">
                <div className="skeleton h-4 w-24 rounded" />
                <div className="skeleton h-16 w-32 rounded" />
                <div className="skeleton h-10 w-full rounded" />
                <div className="space-y-2 pt-3">
                  <div className="skeleton h-2 w-full rounded" />
                  <div className="skeleton h-2 w-full rounded" />
                  <div className="skeleton h-2 w-full rounded" />
                </div>
                <div className="skeleton mt-4 h-10 w-full rounded" />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="border-b border-surface-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="skeleton h-20 rounded-xl" />
            ))}
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[200px_minmax(0,1fr)_280px] lg:gap-10 xl:gap-14">
          <div className="hidden lg:block space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton h-4 w-3/4 rounded" />
            ))}
          </div>
          <div className="space-y-10">
            <div className="skeleton h-8 w-1/2 rounded" />
            <div className="skeleton h-4 w-full rounded" />
            <div className="skeleton h-4 w-full rounded" />
            <div className="skeleton h-4 w-4/5 rounded" />
            <div className="skeleton h-8 w-1/2 rounded pt-6" />
            <div className="skeleton h-4 w-full rounded" />
            <div className="skeleton h-4 w-4/5 rounded" />
          </div>
          <div className="hidden lg:block space-y-4">
            <div className="skeleton h-40 rounded-xl" />
            <div className="skeleton h-32 rounded-xl" />
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

/* ─── Error state — network / server failure. Distinct from
   NotFoundView (which is the legitimate 404 case). ───────────── */
const ErrorView = ({ onRetry }) => (
  <div className="min-h-screen bg-white">
    <Header />
    <main
      data-testid={DETAIL_PAGE.error}
      role="alert"
      aria-live="assertive"
      className="mx-auto flex max-w-3xl flex-col items-center justify-center px-6 py-32 text-center"
    >
      <svg className="mb-5 h-12 w-12 text-risk-700" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 9v3.75m0 4.5v.008M4.5 19.5h15a1.5 1.5 0 0 0 1.3-2.25L13.3 4.5a1.5 1.5 0 0 0-2.6 0L3.2 17.25a1.5 1.5 0 0 0 1.3 2.25Z"
          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
        />
      </svg>
      <h1 className="text-3xl font-bold tracking-tight text-ink-900 sm:text-4xl">
        تعذّر تحميل تفاصيل الفرصة
      </h1>
      <p className="mt-4 max-w-md text-base text-ink-500">
        حدث خطأ أثناء الاتصال بالخادم. تحقّق من الاتصال ثم أعد المحاولة.
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={onRetry}
          data-testid={DETAIL_PAGE.errorRetry}
          className="inline-flex items-center gap-1.5 rounded-lg bg-fursa-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-fursa-800"
        >
          إعادة المحاولة
        </button>
        <Link
          to="/opportunities"
          className="inline-flex items-center gap-1.5 rounded-lg border border-surface-200 bg-white px-5 py-2.5 text-sm font-medium text-ink-900 hover:bg-surface-50"
        >
          العودة إلى الفرص
        </Link>
      </div>
    </main>
    <Footer />
  </div>
);

const NotFoundView = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <main
      data-testid={DETAIL_PAGE.notFound}
      className="mx-auto flex max-w-3xl flex-col items-center justify-center px-6 py-32 text-center"
    >
      <p className="text-xs font-medium uppercase tracking-wider text-ink-500">
        غير موجود
      </p>
      <h1 className="mt-3 text-3xl font-bold tracking-tight text-ink-900 sm:text-4xl">
        هذه الفرصة غير متوفّرة
      </h1>
      <p className="mt-4 max-w-md text-base text-ink-500">
        ربما تكون الفرصة قد أُرشِفت أو أنّ الرابط غير صحيح. يمكنك الرجوع إلى قائمة
        الفرص المتاحة.
      </p>
              <Link
                to="/opportunities"
                data-testid={DETAIL_PAGE.backToList}
                className="mt-8 inline-flex items-center gap-1.5 rounded-lg bg-fursa-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-fursa-800"
              >
                العودة إلى الفرص
                <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
              </Link>
    </main>
    <Footer />
  </div>
);

export default function OpportunityDetailPage() {
  const { id } = useParams();
  const { opportunity, isLoading, error, refresh } = useOpportunity(id);
  const { opportunities: allOpps } = useOpportunities();
  const related = useMemo(
    () => (opportunity ? resolveRelated(opportunity.relatedIds, allOpps) : []),
    [opportunity, allOpps],
  );
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (opportunity) setSaved(isSaved(opportunity.id));
  }, [opportunity]);

  if (isLoading) return <DetailSkeleton />;
  if (error) return <ErrorView onRetry={refresh} />;
  if (!opportunity) return <NotFoundView />;

  const o = opportunity;
  const meta = o.meta ?? {};
  const impact = meta.marketImpact ? MARKET_IMPACT_MAP[meta.marketImpact] : null;
  const time = meta.timeSensitivity
    ? TIME_SENSITIVITY_MAP[meta.timeSensitivity]
    : null;
  const evidenceComp = deriveEvidenceComposition(o);

  const handleSave = () => {
    const nowSaved = toggleSaved(o.id);
    setSaved(nowSaved);
    toast(nowSaved ? "تم حفظ الفرصة" : "أزلت الفرصة من المحفوظات");
  };

  const handleShare = async () => {
    const shareData = { title: o.title, text: o.summary, url: window.location.href };
    try {
      if (navigator.share) return await navigator.share(shareData);
      await navigator.clipboard.writeText(shareData.url);
      toast("تم نسخ رابط الفرصة");
    } catch {
      toast.error("تعذّرت المشاركة الآن.");
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast("تم نسخ رابط الفرصة");
    } catch {
      toast.error("تعذّر نسخ الرابط.");
    }
  };

  const handleContact = () => {
    toast("طلب المعلومات — قريباً", {
      description: "ميزة التواصل المباشر قيد التطوير في نسخة Beta.",
    });
  };

  return (
    <div className="min-h-screen bg-surface-50 text-ink-900">
      <Header />
      <main data-testid={DETAIL_PAGE.root}>
        {/* INTELLIGENCE DOSSIER HEADER — deep navy briefing panel */}
        <section className="fursa-briefing-bg text-white">
          <div className="mx-auto max-w-7xl px-6 py-14 lg:px-8 lg:py-20">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 lg:gap-12">
              <div className="lg:col-span-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-intel-700 bg-intel-900/60 px-3 py-1 text-[11px] font-medium tracking-wider text-intel-100">
                    <Radar className="h-3 w-3" strokeWidth={1.75} />
                    INTELLIGENCE BRIEF
                  </span>
                  <StatusBadge opportunity={o} meta={meta} variant="solid" size="sm" />
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <span
                    data-testid={DETAIL_PAGE.type}
                    className="inline-flex items-center gap-1.5 rounded-full border border-intel-700 bg-intel-900/40 px-3 py-1 text-xs font-medium text-intel-100"
                  >
                    <CircleDot className="h-3 w-3" strokeWidth={2} />
                    {o.type}
                  </span>
                  <span
                    data-testid={DETAIL_PAGE.country}
                    className="inline-flex items-center gap-1.5 rounded-full border border-intel-700 bg-intel-900/40 px-3 py-1 text-xs font-medium text-intel-100"
                  >
                    <MapPin className="h-3 w-3" strokeWidth={1.75} />
                    {o.country}
                  </span>
                  <span
                    data-testid={DETAIL_PAGE.industry}
                    className="inline-flex items-center gap-1.5 rounded-full border border-intel-700 bg-intel-900/40 px-3 py-1 text-xs font-medium text-intel-100"
                  >
                    <Building2 className="h-3 w-3" strokeWidth={1.75} />
                    {o.industry}
                  </span>
                </div>

                <h1
                  data-testid={DETAIL_PAGE.title}
                  className="mt-6 text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl"
                >
                  {o.title}
                </h1>

                <p
                  data-testid={DETAIL_PAGE.summary}
                  className="mt-5 max-w-3xl text-lg leading-relaxed text-intel-100"
                >
                  {o.summary}
                </p>

                <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-intel-200">
                  {o.publishedAt && (
                    <span
                      data-testid={DETAIL_PAGE.publishedAt}
                      className="inline-flex items-center gap-1.5"
                    >
                      <Calendar className="h-3.5 w-3.5" strokeWidth={1.75} />
                      نُشرت {o.publishedAt}
                    </span>
                  )}
                  {o.source && (
                    <span className="inline-flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5" strokeWidth={1.75} />
                      {o.source}
                    </span>
                  )}
                </div>

                {/* Recommended Action — the "so what?" line, prominent */}
                {meta.recommendedAction && (
                  <div className="mt-8 rounded-xl border border-intel-700 bg-intel-900/50 p-5">
                    <p className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-opportunity-500">
                      <Target className="h-3.5 w-3.5" strokeWidth={2} />
                      الإجراء المقترح
                    </p>
                    <p className="mt-2 text-base font-medium leading-relaxed text-white">
                      {meta.recommendedAction}
                    </p>
                  </div>
                )}
              </div>

              {/* Score / action sidebar */}
              <aside className="lg:col-span-1">
                <div className="rounded-2xl border border-intel-700 bg-white p-6 text-ink-900 lg:sticky lg:top-24">
                  <p className="text-xs font-medium uppercase tracking-wider text-ink-500">
                    نقاط الفرصة
                  </p>
                  <div
                    dir="ltr"
                    className="mt-3 flex items-baseline justify-start gap-2 font-mono"
                  >
                    <span
                      data-testid={DETAIL_PAGE.score}
                      className="text-6xl font-semibold tracking-tight text-intel-800"
                    >
                      {o.score}
                    </span>
                    <span className="text-2xl text-ink-500">/ 100</span>
                  </div>
                  <div className="mt-3">
                    <ScoreBadge score={o.score} size="sm" />
                  </div>

                  {typeof meta.confidence === "number" && (
                    <div className="mt-5 rounded-lg border border-surface-200 bg-surface-50 p-3">
                      <div className="flex items-center justify-between text-xs">
                        <span className="inline-flex items-center gap-1.5 text-ink-500">
                          <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.75} />
                          مستوى الثقة
                        </span>
                        <span
                          dir="ltr"
                          className="font-mono font-semibold text-intel-800"
                        >
                          {meta.confidence}%
                        </span>
                      </div>
                      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface-200">
                        <div
                          className="h-full rounded-full bg-intel-800"
                          style={{ width: `${meta.confidence}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Score breakdown — how the 0-100 was computed */}
                  <div className="mt-5" data-testid="detail-score-breakdown">
                    <p className="text-[11px] font-medium uppercase tracking-wider text-ink-500">
                      كيف حُسبت النقاط؟
                    </p>
                    <ul className="mt-3 space-y-2.5">
                      {deriveSubscores(o).map((s) => (
                        <li key={s.key}>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-ink-700">{s.label}</span>
                            <span
                              dir="ltr"
                              className="font-mono font-semibold text-ink-900"
                            >
                              {s.value}
                              <span className="text-ink-500"> / 100</span>
                            </span>
                          </div>
                          <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-surface-100">
                            <div
                              className={`h-full rounded-full ${
                                s.value >= 80
                                  ? "bg-opportunity-500"
                                  : s.value >= 65
                                    ? "bg-warning-500"
                                    : "bg-risk-500"
                              }`}
                              style={{ width: `${s.value}%` }}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-3 text-[11px] leading-relaxed text-ink-500">
                      المعادلة: مجموع مرجّح للعوامل الستة وفق مرجع
                      <span className="mx-1">docs/OpportunityScoring.md</span>.
                    </p>
                  </div>

                  <div className="mt-6 space-y-3">
                    <button
                      type="button"
                      onClick={handleContact}
                      data-testid={DETAIL_PAGE.ctaContact}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-fursa-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-fursa-800"
                    >
                      <MessageCircle className="h-4 w-4" strokeWidth={1.75} />
                      اطلب مزيداً من المعلومات
                    </button>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={handleSave}
                        aria-pressed={saved}
                        data-testid={DETAIL_PAGE.ctaSave}
                        className={`inline-flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-2 text-xs font-semibold transition-colors ${
                          saved
                            ? "border-fursa-700 bg-fursa-700 text-white hover:bg-fursa-800"
                            : "border-surface-200 bg-white text-ink-900 hover:border-fursa-300 hover:bg-fursa-50"
                        }`}
                      >
                        {saved ? (
                          <BookmarkCheck className="h-3.5 w-3.5" strokeWidth={1.75} />
                        ) : (
                          <BookmarkPlus className="h-3.5 w-3.5" strokeWidth={1.75} />
                        )}
                        {saved ? "محفوظة" : "حفظ"}
                      </button>
                      <button
                        type="button"
                        onClick={handleShare}
                        data-testid={DETAIL_PAGE.ctaShare}
                        className="inline-flex items-center justify-center gap-1.5 rounded-md border border-surface-200 bg-white px-2.5 py-2 text-xs font-medium text-ink-900 hover:bg-surface-50"
                      >
                        <Share2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                        مشاركة
                      </button>
                      <button
                        type="button"
                        onClick={handleCopyLink}
                        data-testid={DETAIL_PAGE.ctaCopyLink}
                        className="inline-flex items-center justify-center gap-1.5 rounded-md border border-surface-200 bg-white px-2.5 py-2 text-xs font-medium text-ink-900 hover:bg-surface-50"
                      >
                        <Link2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                        نسخ
                      </button>
                    </div>
                  </div>

                  {o.tags?.length > 0 && (
                    <div className="mt-6" data-testid={DETAIL_PAGE.tags}>
                      <p className="text-[11px] font-medium uppercase tracking-wider text-ink-500">
                        وسوم
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {o.tags.map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center rounded-full border border-surface-200 bg-white px-2.5 py-1 text-[11px] font-medium text-ink-700"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Intelligence Metrics strip — bright surface, between the dark hero and body */}
        <section className="border-b border-surface-200 bg-white">
          <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <IntelligenceMetric
                icon={TrendingUp}
                label="الأثر السوقي"
                value={impact?.label ?? "—"}
                tone="intel"
                align="stack"
              />
              <IntelligenceMetric
                icon={Clock}
                label="الإلحاح الزمني"
                value={time?.label ?? "—"}
                tone={time?.tone === "risk" ? "risk" : time?.tone === "warning" ? "warning" : "intel"}
                align="stack"
              />
              <IntelligenceMetric
                icon={Coins}
                label="رأس المال المطلوب"
                value={meta.capitalRequired ?? "—"}
                tone="neutral"
                align="stack"
              />
              <IntelligenceMetric
                icon={ShieldCheck}
                label="مستوى الثقة"
                value={
                  typeof meta.confidence === "number"
                    ? `${meta.confidence}%`
                    : "—"
                }
                tone="opportunity"
                align="stack"
              />
            </div>
          </div>
        </section>

        {/* Dossier body — 3-column with sticky ToC + body + sources rail */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[200px_minmax(0,1fr)_280px] lg:gap-10 xl:gap-14">
            {/* ── Left: sticky ToC ── */}
            <TableOfContents
              items={[
                meta.whatHappened            && { id: "what-happened",   label: "الملخّص" },
                (meta.whyItMatters || o.executiveSummary) && { id: "why-matters",     label: "التحليل" },
                o.suggestedSteps?.length     && { id: "suggested-steps", label: "الإجراء" },
                meta.risks?.length           && { id: "risks",           label: "المخاطر" },
                o.keyIndicators?.length      && { id: "key-indicators",  label: "المؤشرات" },
                evidenceComp                 && { id: "evidence",        label: "الأدلّة" },
                o.sources?.length            && { id: "sources",         label: "المصادر" },
                related.length               && { id: "related",         label: "ذات صلة" },
              ].filter(Boolean)}
            />

            {/* ── Center: editorial body ── */}
            <div className="min-w-0 space-y-16">
              {/* What Happened */}
              {meta.whatHappened && (
                <div>
                  <SectionHeading eyebrow="1 · الملخّص" title="ما الذي حدث؟" icon={Radar} id="what-happened" />
                  <p className="text-base leading-loose text-ink-700 sm:text-[17px]">
                    {meta.whatHappened}
                  </p>
                </div>
              )}

              {/* Why It Matters */}
              {(meta.whyItMatters || o.executiveSummary) && (
                <div>
                  <SectionHeading eyebrow="2 · التحليل" title="لماذا يهمّ هذا الآن؟" icon={Scale} id="why-matters" />
                  <p
                    data-testid={DETAIL_PAGE.executiveSummary}
                    className="text-base leading-loose text-ink-700 sm:text-[17px]"
                  >
                    {meta.whyItMatters ?? o.executiveSummary}
                  </p>
                </div>
              )}

              {/* Suggested Steps */}
              {o.suggestedSteps?.length > 0 && (
                <div data-testid={DETAIL_PAGE.suggestedSteps}>
                  <SectionHeading eyebrow="3 · الإجراء" title="خطوات مقترحة" icon={CheckCircle2} id="suggested-steps" />
                  <ol className="space-y-3">
                    {o.suggestedSteps.map((step, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-4 rounded-xl border border-surface-200 bg-white p-4 shadow-card transition-colors hover:border-fursa-200"
                      >
                        <span
                          dir="ltr"
                          className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-fursa-50 font-mono text-xs font-bold text-fursa-700 ring-1 ring-fursa-200"
                        >
                          {i + 1}
                        </span>
                        <span className="text-sm leading-relaxed text-ink-900 sm:text-[15px]">
                          {step}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Risks */}
              {meta.risks?.length > 0 && (
                <div data-testid="detail-risks">
                  <SectionHeading eyebrow="4 · المخاطر" title="تقييم المخاطر" icon={AlertTriangle} id="risks" />
                  <div className="space-y-3">
                    {meta.risks.map((r, i) => {
                      const tone = RISK_TONE[r.level] ?? RISK_TONE.medium;
                      return (
                        <div
                          key={i}
                          className={`rounded-xl border ${tone.border} ${tone.bg} p-4`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2.5">
                              <span
                                className={`inline-flex h-7 w-7 items-center justify-center rounded-lg ${tone.icon} text-white`}
                              >
                                <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.75} />
                              </span>
                              <span className={`text-sm font-semibold ${tone.text}`}>
                                {r.title}
                              </span>
                            </div>
                            <span
                              className={`inline-flex items-center rounded-full border ${tone.border} bg-white/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${tone.text}`}
                            >
                              خطر {tone.label}
                            </span>
                          </div>
                          {r.detail && (
                            <p className="mt-2 text-sm leading-relaxed text-ink-700">
                              {r.detail}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Key Indicators */}
              {o.keyIndicators?.length > 0 && (
                <div data-testid={DETAIL_PAGE.keyIndicators}>
                  <SectionHeading eyebrow="5 · البيانات" title="المؤشرات الرئيسية" icon={TrendingUp} id="key-indicators" />
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {o.keyIndicators.map((k, i) => (
                      <div
                        key={k.label}
                        data-testid={DETAIL_PAGE.keyIndicator(i)}
                        className="rounded-xl border border-surface-200 bg-white px-4 py-4 shadow-card transition-all hover:-translate-y-0.5 hover:border-fursa-200 hover:shadow-card-hover"
                      >
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">
                          {k.label}
                        </p>
                        <p
                          dir="ltr"
                          className="mt-1.5 text-right text-lg font-bold tracking-tight text-ink-900 sm:text-xl"
                        >
                          {k.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Right: Sources rail + Metadata (desktop) ── */}
            <aside className="space-y-6">
              {evidenceComp && (
                <div
                  id="evidence"
                  data-testid="detail-evidence-composition"
                  className="rounded-xl border border-surface-200 bg-white p-5 shadow-card scroll-mt-24"
                >
                  <div className="flex items-center justify-between gap-2 border-b border-surface-100 pb-3">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-3.5 w-3.5 text-intel-800" strokeWidth={2} aria-hidden />
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-intel-800">
                        تركيبة الأدلّة
                      </p>
                    </div>
                    <span
                      dir="ltr"
                      className="num font-mono text-[11px] font-semibold tabular-nums text-ink-500"
                    >
                      {evidenceComp.total} claim{evidenceComp.total !== 1 ? "s" : ""}
                    </span>
                  </div>

                  {/* Segmented bar — proportional, non-color-only via legend below */}
                  <div
                    className="mt-4 flex h-2 overflow-hidden rounded-full bg-surface-100"
                    role="img"
                    aria-label={`تركيبة الأدلّة: ${evidenceComp.verified} موثّق، ${evidenceComp.estimated} تقديري، ${evidenceComp.expert_assessment} رأي مهني`}
                  >
                    {evidenceComp.verified > 0 && (
                      <span
                        data-testid="detail-evidence-bar-verified"
                        className="block h-full bg-fursa-500"
                        style={{ width: `${(evidenceComp.verified / evidenceComp.total) * 100}%` }}
                      />
                    )}
                    {evidenceComp.estimated > 0 && (
                      <span
                        data-testid="detail-evidence-bar-estimated"
                        className="block h-full bg-intel-500"
                        style={{ width: `${(evidenceComp.estimated / evidenceComp.total) * 100}%` }}
                      />
                    )}
                    {evidenceComp.expert_assessment > 0 && (
                      <span
                        data-testid="detail-evidence-bar-expert_assessment"
                        className="block h-full bg-gold-500"
                        style={{ width: `${(evidenceComp.expert_assessment / evidenceComp.total) * 100}%` }}
                      />
                    )}
                    {evidenceComp.unknown > 0 && (
                      <span
                        data-testid="detail-evidence-bar-unknown"
                        className="block h-full bg-surface-300"
                        style={{ width: `${(evidenceComp.unknown / evidenceComp.total) * 100}%` }}
                      />
                    )}
                  </div>

                  {/* Per-class rows — icon + label + hint + count + % */}
                  <ul className="mt-4 space-y-2.5">
                    {["verified", "estimated", "expert_assessment", "unknown"]
                      .filter((k) => evidenceComp[k] > 0)
                      .map((k) => {
                        const c = EVIDENCE_CLASSES[k];
                        const Icon = c.icon;
                        const pct = Math.round((evidenceComp[k] / evidenceComp.total) * 100);
                        return (
                          <li
                            key={k}
                            data-testid={`detail-evidence-row-${k}`}
                            className="flex items-center justify-between gap-3"
                          >
                            <span className="inline-flex items-center gap-2 text-[13px] text-ink-700">
                              <Icon className={`h-3.5 w-3.5 ${c.iconTone}`} strokeWidth={1.75} aria-hidden />
                              <span className="font-medium text-ink-900">{c.label}</span>
                              <span className="hidden text-[11px] text-ink-500 sm:inline">· {c.hint}</span>
                            </span>
                            <span className="inline-flex items-baseline gap-2">
                              <span
                                dir="ltr"
                                className="num font-mono text-sm font-semibold tabular-nums text-ink-900"
                              >
                                {evidenceComp[k]}
                              </span>
                              <span
                                dir="ltr"
                                data-testid={`detail-evidence-pct-${k}`}
                                className="num font-mono text-[11px] tabular-nums text-ink-500"
                              >
                                {pct}%
                              </span>
                            </span>
                          </li>
                        );
                      })}
                  </ul>

                  <p className="mt-4 border-t border-surface-100 pt-3 text-[11px] leading-relaxed text-ink-500">
                    تصنيف مُشتقّ من مصادر الفرصة ومؤشراتها الرئيسية وتقييم المخاطر —
                    <span className="whitespace-nowrap"> يُقرأ إلى جانب </span>
                    <a href="#sources" className="text-ink-700 underline decoration-surface-300 underline-offset-4 hover:text-intel-900">
                      قائمة المصادر
                    </a>.
                  </p>
                </div>
              )}

              {o.sources?.length > 0 && (
                <div
                  id="sources"
                  data-testid={DETAIL_PAGE.sources}
                  className="rounded-xl border border-surface-200 bg-white p-5 shadow-card scroll-mt-24"
                >
                  <div className="flex items-center gap-2 border-b border-surface-100 pb-3">
                    <FileText className="h-3.5 w-3.5 text-fursa-700" strokeWidth={1.75} />
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-fursa-700">
                      المصادر
                    </p>
                  </div>
                  <ul className="mt-3 space-y-3">
                    {o.sources.map((s) => (
                      <li key={s.name} className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-ink-900">{s.name}</span>
                        <span className="inline-flex w-fit items-center rounded-full bg-fursa-50 px-2 py-0.5 text-[10px] font-semibold text-fursa-700">
                          {s.type}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 border-t border-surface-100 pt-3 text-[11px] leading-relaxed text-ink-500">
                    جميع المصادر مُتحقَّق منها يدوياً من قِبل فريق التحرير.
                  </p>
                </div>
              )}

              <div className="rounded-xl border border-surface-200 bg-white p-5 shadow-card">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">
                  البيانات الأساسية
                </p>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-ink-500">النوع</dt>
                    <dd className="text-right font-medium text-ink-900">{o.type}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-ink-500">الدولة</dt>
                    <dd className="text-right font-medium text-ink-900">{o.country}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-ink-500">القطاع</dt>
                    <dd className="text-right font-medium text-ink-900">{o.industry}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-ink-500">النقاط</dt>
                    <dd dir="ltr" className="font-mono font-bold tabular-nums text-fursa-700">
                      {o.score} / 100
                    </dd>
                  </div>
                  {o.publishedAt && (
                    <div className="flex items-start justify-between gap-4">
                      <dt className="text-ink-500">النشر</dt>
                      <dd className="text-right font-medium text-ink-900">{o.publishedAt}</dd>
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-ink-500">المُعرّف</dt>
                    <dd dir="ltr" className="font-mono text-[11px] uppercase tracking-widest text-ink-500">
                      #{o.id}
                    </dd>
                  </div>
                </dl>
                <p className="mt-4 border-t border-surface-100 pt-3 text-[11px] leading-relaxed text-ink-500">
                  البيانات إرشادية ومستمدّة من مصادر عامة. يُنصح بإجراء فحص نافٍ للجهالة قبل أي التزام مالي.
                </p>
              </div>

              <div className="pt-2" data-testid={DETAIL_PAGE.backToList + "-sidebar"}>
                <BackButton
                  fallback="/opportunities"
                  label="العودة إلى قائمة الفرص"
                  testId="detail-back-to-list"
                  className="w-full justify-center"
                />
              </div>
            </aside>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section
            id="related"
            data-testid={DETAIL_PAGE.related}
            className="border-t border-surface-200 bg-surface-50 scroll-mt-24"
          >
            <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
              <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="fursa-eyebrow">Related</p>
                  <h2 className="mt-3 text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
                    فرص أخرى قد تهمّك
                  </h2>
                </div>
                <Link
                  to="/opportunities"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-fursa-700 transition-colors hover:text-fursa-800"
                >
                  عرض الكل
                  <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
                </Link>
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {related.map((r) => (
                  <OpportunityCard key={r.id} opportunity={r} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
